import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePerformanceCycle } from '../contexts/PerformanceCycleContext';
import { goalsApi, reviewsApi, performanceCycleApi, goalLibraryApi } from '../lib/api';
import { hasAllKpiComments } from '../lib/reviewGates';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Alert, AlertDescription } from '../components/ui/alert';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { format, isValid } from 'date-fns';
import {
  Plus, Edit, Trash2, Send, Loader2, Target, Briefcase, Users, X,
  ChevronDown, ChevronUp, CheckCircle, XCircle,
  Award, TrendingUp, Download, FileText, Info, Calendar as CalendarIcon, Star,
  Lock, ClipboardList, Flag, Save, Check, Clock, UserCheck, BarChart3, PenTool, AlertCircle, BookOpen, Layers
} from 'lucide-react';

// Status configuration
const goalStatusConfig = {
  draft: { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
  pending_approval: { color: 'bg-amber-100 text-amber-700', label: 'Pending Approval' },
  approved: { color: 'bg-emerald-100 text-emerald-700', label: 'Approved' },
  rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' },
};

// A goal the manager sends back returns to "draft" but keeps manager_feedback, so it
// is editable/deletable/resubmittable through the normal draft flow. A brand-new draft
// has no manager_feedback. Legacy goals may still carry the old "rejected" status.
// Any of these means "the employee needs to revise and resubmit".
const needsRevision = (g) => g.status === 'rejected' || (g.status === 'draft' && !!g.manager_feedback);

// KPI timelines aren't always a parseable date (bad imports, freeform text) - fall back to the raw value instead of crashing
const formatKpiDeadline = (timeline) => {
  if (!timeline) return null;
  const date = new Date(timeline);
  return isValid(date) ? format(date, 'MMM d, yyyy') : timeline;
};

// Rating configuration
const ratingColors = {
  1: 'bg-red-500', 2: 'bg-orange-500', 3: 'bg-blue-500', 4: 'bg-green-500', 5: 'bg-teal-500',
};

// Year Selector with lock support
const YearSelector = ({ selectedYear, onYearChange, availableYears, cyclesData }) => {
  return (
    <div className="flex items-center gap-1 bg-white rounded-lg border p-1">
      <div className="flex gap-1">
        {availableYears.map((year) => {
          const cycleData = cyclesData?.find(c => c.year === year);
          const isLocked = !cycleData?.is_year_active;
          const isSelected = selectedYear === year;
          
          return (
            <Button 
              key={year} 
              variant={isSelected ? 'default' : 'ghost'} 
              size="sm" 
              onClick={() => !isLocked && onYearChange(year)} 
              disabled={isLocked}
              className={`h-7 px-2 text-xs relative ${isSelected ? 'bg-primary text-primary-foreground' : ''} ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {year}
              {isLocked && <Lock className="h-3 w-3 ml-1" />}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

// Inline Goal Form Component
export const InlineGoalForm = ({ goalType, onSave, onCancel, editingGoal = null, libraryGoal = null }) => {
  const isLibraryMode = !!libraryGoal;

  const initKpis = () => {
    if (isLibraryMode) {
      // Initialize from library goal kpis; measurement/timeline editable, name/weight locked
      return (libraryGoal.kpis || []).map((kpi) => {
        const saved = editingGoal?.kpis?.find((k) => k.name === kpi.name);
        return {
          name: kpi.name,
          measurement: saved?.measurement || '',
          timeline: saved?.timeline || kpi.timeline || '',
          weight: kpi.weight,
        };
      });
    }
    return editingGoal?.kpis?.length > 0 ? editingGoal.kpis : [{ name: '', measurement: '', timeline: '', weight: '' }];
  };

  const [title, setTitle] = useState(isLibraryMode ? libraryGoal.title : (editingGoal?.title || ''));
  const [kpis, setKpis] = useState(initKpis);
  const [saving, setSaving] = useState(false);

  const totalWeight = kpis.reduce((sum, kpi) => sum + (parseFloat(kpi.weight) || 0), 0);

  const addKPI = () => setKpis([...kpis, { name: '', measurement: '', timeline: '', weight: '' }]);

  const removeKPI = (index) => {
    if (kpis.length > 1) setKpis(kpis.filter((_, i) => i !== index));
  };

  const updateKPI = (index, field, value) => {
    const newKpis = [...kpis];
    newKpis[index] = { ...newKpis[index], [field]: value };
    setKpis(newKpis);
  };

  const handleSave = async () => {
    if (isLibraryMode) {
      const hasEmptyMeasurement = kpis.some(kpi => !kpi.measurement);
      if (hasEmptyMeasurement) {
        toast.error('Please fill in the Measurement for all KPIs');
        return;
      }
      setSaving(true);
      await onSave({
        title: libraryGoal.title,
        goal_type: 'pre_approved',
        kpis,
        library_goal_id: libraryGoal.id,
      });
      setSaving(false);
      return;
    }

    if (!title.trim()) {
      toast.error('Please enter a goal title');
      return;
    }
    const hasEmptyKPI = kpis.some(kpi => !kpi.name || !kpi.measurement || !kpi.timeline || !kpi.weight);
    if (hasEmptyKPI) {
      toast.error('Please fill all KPI fields');
      return;
    }
    setSaving(true);
    await onSave({ title, goal_type: goalType, kpis });
    setSaving(false);
  };

  if (isLibraryMode) {
    return (
      <div className="border rounded-lg p-4 bg-white space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-emerald-600" />
            Fill In Goal
          </h4>
          <Badge variant="outline" className="text-xs">Weight: {totalWeight}%</Badge>
        </div>

        <div className="text-sm text-slate-600 font-medium bg-slate-50 rounded px-3 py-2 border">
          Library Goal: {libraryGoal.title}
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">KPIs</Label>
          {kpis.map((kpi, index) => (
            <div key={index} className="grid grid-cols-5 gap-2 items-center">
              <Input placeholder="KPI Name" value={kpi.name} disabled className="col-span-1 bg-slate-50" />
              <Input
                placeholder="Measurement / Target"
                value={kpi.measurement}
                onChange={(e) => updateKPI(index, 'measurement', e.target.value)}
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start text-left font-normal h-9 text-sm">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {kpi.timeline ? formatKpiDeadline(kpi.timeline) : <span className="text-muted-foreground">Deadline</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" side="bottom" sideOffset={4} collisionPadding={12}>
                  <Calendar
                    mode="single"
                    selected={kpi.timeline && isValid(new Date(kpi.timeline)) ? new Date(kpi.timeline) : undefined}
                    onSelect={(date) => updateKPI(index, 'timeline', date ? format(date, 'yyyy-MM-dd') : '')}
                    initialFocus
                    className="rounded-md border"
                  />
                </PopoverContent>
              </Popover>
              <Input
                type="number"
                value={kpi.weight?.toString() || ''}
                disabled
                className="h-9 bg-slate-50 text-center"
              />
              <div className="h-8 w-8" />
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 bg-white space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm flex items-center gap-2">
          {goalType === 'business' ? <Briefcase className="h-4 w-4 text-blue-600" /> : <Users className="h-4 w-4 text-purple-600" />}
          {editingGoal ? 'Edit' : 'New'} {goalType === 'business' ? 'Business' : 'People'} Goal
        </h4>
        <Badge variant="outline" className="text-xs">Weight: {totalWeight}%</Badge>
      </div>

      <Input placeholder="Goal Title" value={title} onChange={(e) => setTitle(e.target.value)} />

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">KPIs</Label>
        {kpis.map((kpi, index) => (
          <div key={index} className="grid grid-cols-5 gap-2 items-center">
            <Input placeholder="KPI Name" value={kpi.name} onChange={(e) => updateKPI(index, 'name', e.target.value)} className="col-span-1" />
            <Input placeholder="Measurement" value={kpi.measurement} onChange={(e) => updateKPI(index, 'measurement', e.target.value)} />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start text-left font-normal h-9 text-sm">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {kpi.timeline ? formatKpiDeadline(kpi.timeline) : <span className="text-muted-foreground">Deadline</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start" side="bottom" sideOffset={4} collisionPadding={12}>
                <Calendar
                  mode="single"
                  selected={kpi.timeline && isValid(new Date(kpi.timeline)) ? new Date(kpi.timeline) : undefined}
                  onSelect={(date) => updateKPI(index, 'timeline', date ? format(date, 'yyyy-MM-dd') : '')}
                  initialFocus
                  className="rounded-md border"
                />
              </PopoverContent>
            </Popover>
            <SearchableSelect
              value={kpi.weight?.toString() || ''}
              onValueChange={(value) => updateKPI(index, 'weight', value)}
              placeholder="Weight %"
              className="h-9"
              options={[5, 10, 15, 20, 25, 30, 35, 40, 45, 50].map(w => ({ value: w.toString(), label: `${w}%` }))}
            />
            <Button variant="ghost" size="sm" onClick={() => removeKPI(index)} disabled={kpis.length <= 1} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addKPI} className="w-full border-dashed text-xs">
          <Plus className="h-3 w-3 mr-1" /> Add KPI
        </Button>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Save
        </Button>
      </div>
    </div>
  );
};

// Goal Card Component
export const GoalCard = ({ goal, onEdit, onDelete, canEdit }) => {
  return (
    <div className={`p-3 rounded-lg border ${needsRevision(goal) ? 'border-red-300 bg-red-50/50' : 'bg-white'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {goal.goal_type === 'pre_approved' ? <BookOpen className="h-3.5 w-3.5 text-emerald-600" /> : goal.goal_type === 'business' ? <Briefcase className="h-3.5 w-3.5 text-blue-600" /> : <Users className="h-3.5 w-3.5 text-purple-600" />}
            <span className="font-medium text-sm">{goal.title}</span>
            <Badge variant="outline" className="text-xs">{goal.weight}%</Badge>
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => onEdit(goal)} className="h-7 w-7 p-0">
              <Edit className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(goal.id)} className="h-7 w-7 p-0 text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
      
      {/* KPIs Table */}
      {goal.kpis && goal.kpis.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left p-2 font-medium text-muted-foreground border-b">KPI</th>
                <th className="text-left p-2 font-medium text-muted-foreground border-b">Measurement</th>
                <th className="text-left p-2 font-medium text-muted-foreground border-b">Deadline</th>
                <th className="text-right p-2 font-medium text-muted-foreground border-b">Weight</th>
              </tr>
            </thead>
            <tbody>
              {goal.kpis.map((kpi, index) => (
                <tr key={kpi.id || index} className="border-b border-slate-100 last:border-0">
                  <td className="p-2 text-slate-700">{kpi.name}</td>
                  <td className="p-2 text-slate-600">{kpi.measurement}</td>
                  <td className="p-2 text-slate-600">
                    {kpi.timeline ? formatKpiDeadline(kpi.timeline) : '-'}
                  </td>
                  <td className="p-2 text-right font-medium text-slate-700">{kpi.weight}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Requirement Check Component with helper text
const RequirementCheck = ({ met, label, helperText }) => (
  <div className={`${met ? 'text-emerald-700' : 'text-muted-foreground'}`}>
    <div className="flex items-center gap-2 text-sm">
      {met ? <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" /> : <div className="h-4 w-4 rounded-full border-2 border-gray-300 flex-shrink-0" />}
      <span>{label}</span>
    </div>
    {helperText && (
      <p className="text-xs text-muted-foreground ml-6 mt-0.5">{helperText}</p>
    )}
  </div>
);

// Review Comment Section
// Optimized Review Section - Shows each goal ONCE with Mid-Year and Year-End comments
const ReviewSection = ({ goals, review, midYearComments, setMidYearComments, yearEndComments, setYearEndComments, onSubmitMidYear, onSubmitYearEnd, submitting, showMidYear, showYearEnd, hideMidYearSubmit = false }) => {
  // Get existing feedback from review
  const midYearFeedback = review?.mid_year_feedback || {};
  const yearEndFeedback = review?.year_end_feedback || {};
  const midYearEmployeeFeedback = midYearFeedback.employee_feedback || {};
  const midYearManagerFeedback = midYearFeedback.manager_feedback || {};
  const yearEndEmployeeFeedback = yearEndFeedback.employee_feedback || {};
  const yearEndManagerFeedback = yearEndFeedback.manager_feedback || {};
  
  const hasMidYearEmployeeSubmitted = Object.keys(midYearEmployeeFeedback).length > 0;
  const hasMidYearManagerSubmitted = Object.keys(midYearManagerFeedback).length > 0;
  const hasYearEndEmployeeSubmitted = Object.keys(yearEndEmployeeFeedback).length > 0;
  const hasYearEndManagerSubmitted = Object.keys(yearEndManagerFeedback).length > 0;

  // A mid-year review is FINAL (read-only) only when explicitly submitted.
  // A saved draft (submission_status === 'draft') stays editable so the user can resume.
  // Legacy feedback without a submission_status is treated as final for backward compatibility.
  const isMidYearFinal = hasMidYearEmployeeSubmitted && midYearFeedback.submission_status !== 'draft';
  // The manager's feedback should only be visible to the employee once it's finally submitted (not a draft).
  const isMidYearManagerFinal = hasMidYearManagerSubmitted && midYearFeedback.manager_submission_status !== 'draft';
  
  const allMidYearCommentsFilled = hasAllKpiComments(goals, midYearComments);
  const allYearEndCommentsFilled = hasAllKpiComments(goals, yearEndComments);
  
  return (
    <div className="space-y-4 mt-4">
      <h4 className="font-semibold flex items-center gap-2 text-lg">
        <ClipboardList className="h-5 w-5 text-purple-600" />
        Performance Review
      </h4>
      
      {/* Goal Cards with Review Comments */}
      <div className="space-y-4">
        {goals.map((goal) => (
          <div key={goal.id} className="border rounded-lg bg-white overflow-hidden">
            {/* Goal Header */}
            <div className="bg-slate-50 px-4 py-3 border-b">
              <div className="flex items-center gap-2">
                {goal.goal_type === 'business' ? 
                  <Briefcase className="h-4 w-4 text-blue-600" /> : 
                  <Users className="h-4 w-4 text-purple-600" />
                }
                <span className="font-medium">{goal.title}</span>
                <Badge variant="outline" className="text-xs ml-auto">{goal.weight}%</Badge>
                <Badge variant="outline" className="text-xs">
                  {goal.goal_type === 'business' ? 'Business' : 'People'}
                </Badge>
              </div>
            </div>
            
            {/* KPIs Table with Ratings for Year-End */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">KPI</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Measurement</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Deadline</th>
                    <th className="text-right px-4 py-2 font-medium text-muted-foreground">Weight</th>
                    {showYearEnd && (
                      <th className="text-center px-4 py-2 font-medium text-muted-foreground">Rating</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {goal.kpis?.map((kpi, index) => (
                    <tr key={kpi.id || index} className="border-b last:border-0 hover:bg-gray-50/50">
                      <td className="px-4 py-2 text-slate-800">{kpi.name}</td>
                      <td className="px-4 py-2 text-slate-600">{kpi.measurement}</td>
                      <td className="px-4 py-2 text-slate-600">
                        {kpi.timeline ? formatKpiDeadline(kpi.timeline) : '-'}
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-slate-700">{kpi.weight}%</td>
                      {showYearEnd && (
                        <td className="px-4 py-2 text-center">
                          {kpi.rating ? (
                            <Badge className={`text-xs ${
                              kpi.rating >= 4 ? 'bg-green-100 text-green-700' :
                              kpi.rating === 3 ? 'bg-blue-100 text-blue-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {kpi.rating}/5
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Mid-Year Review Comments (per KPI) */}
            {showMidYear && (
              <div className="border-t">
                <div className="bg-amber-50 px-4 py-2 border-b flex items-center gap-2">
                  <Flag className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-700">Mid-Year Review</span>
                </div>
                {(typeof midYearEmployeeFeedback[goal.id] === 'string' || typeof midYearManagerFeedback[goal.id] === 'string') && (
                  <div className="px-4 py-2 border-b bg-amber-50/40 text-xs text-muted-foreground italic">
                    Previous comment (submitted before per-KPI comments):
                    {typeof midYearEmployeeFeedback[goal.id] === 'string' && (
                      <div className="mt-1"><span className="font-medium not-italic">Employee:</span> {midYearEmployeeFeedback[goal.id]}</div>
                    )}
                    {typeof midYearManagerFeedback[goal.id] === 'string' && (
                      <div className="mt-1"><span className="font-medium not-italic">Manager:</span> {midYearManagerFeedback[goal.id]}</div>
                    )}
                  </div>
                )}
                {goal.kpis?.map((kpi, index) => (
                  <div key={kpi.id || index} className="border-b last:border-0">
                    <div className="px-4 pt-2 text-xs font-semibold text-slate-600">{kpi.name}</div>
                    <div className="grid md:grid-cols-2 gap-0 divide-x">
                      {/* Employee Comment */}
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="h-3.5 w-3.5 text-blue-600" />
                          <span className="text-xs font-medium text-blue-700">My Comment</span>
                          {isMidYearFinal ? (
                            <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted</Badge>
                          ) : midYearComments[kpi.id]?.trim() ? (
                            <Badge className="bg-yellow-100 text-yellow-700 text-xs ml-auto">Draft</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 text-xs ml-auto">Your Turn</Badge>
                          )}
                        </div>
                        {isMidYearFinal ? (
                          <div className="text-sm text-slate-700 bg-blue-50 p-2 rounded border border-blue-100">
                            {midYearEmployeeFeedback[kpi.id] || <span className="text-muted-foreground italic">No comment</span>}
                          </div>
                        ) : (
                          <Textarea
                            placeholder="Add your mid-year progress update..."
                            value={midYearComments[kpi.id] || ''}
                            onChange={(e) => setMidYearComments({ ...midYearComments, [kpi.id]: e.target.value })}
                            rows={2}
                            className="text-sm"
                          />
                        )}
                      </div>
                      {/* Manager Comment */}
                      <div className="p-3 bg-gray-50/50">
                        <div className="flex items-center gap-2 mb-2">
                          <UserCheck className="h-3.5 w-3.5 text-emerald-600" />
                          <span className="text-xs font-medium text-emerald-700">Manager Comment</span>
                          {isMidYearManagerFinal && midYearManagerFeedback[kpi.id] ? (
                            <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-500 text-xs ml-auto">Pending</Badge>
                          )}
                        </div>
                        <div className="text-sm text-slate-700 bg-white p-2 rounded border">
                          {isMidYearManagerFinal
                            ? (midYearManagerFeedback[kpi.id] || <span className="text-muted-foreground italic">Awaiting manager feedback</span>)
                            : <span className="text-muted-foreground italic">Awaiting manager feedback</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Year-End Review Comments (per KPI) */}
            {showYearEnd && (
              <div className="border-t">
                <div className="bg-purple-50 px-4 py-2 border-b flex items-center gap-2">
                  <Award className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-700">Year-End Review</span>
                </div>
                {(typeof yearEndEmployeeFeedback[goal.id] === 'string' || typeof yearEndManagerFeedback[goal.id] === 'string') && (
                  <div className="px-4 py-2 border-b bg-purple-50/40 text-xs text-muted-foreground italic">
                    Previous comment (submitted before per-KPI comments):
                    {typeof yearEndEmployeeFeedback[goal.id] === 'string' && (
                      <div className="mt-1"><span className="font-medium not-italic">Employee:</span> {yearEndEmployeeFeedback[goal.id]}</div>
                    )}
                    {typeof yearEndManagerFeedback[goal.id] === 'string' && (
                      <div className="mt-1"><span className="font-medium not-italic">Manager:</span> {yearEndManagerFeedback[goal.id]}</div>
                    )}
                  </div>
                )}
                {goal.kpis?.map((kpi, index) => (
                  <div key={kpi.id || index} className="border-b last:border-0">
                    <div className="px-4 pt-2 text-xs font-semibold text-slate-600">{kpi.name}</div>
                    <div className="grid md:grid-cols-2 gap-0 divide-x">
                      {/* Employee Comment */}
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="h-3.5 w-3.5 text-blue-600" />
                          <span className="text-xs font-medium text-blue-700">My Comment</span>
                          {yearEndEmployeeFeedback[kpi.id] ? (
                            <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 text-xs ml-auto">Your Turn</Badge>
                          )}
                        </div>
                        {hasYearEndEmployeeSubmitted ? (
                          <div className="text-sm text-slate-700 bg-blue-50 p-2 rounded border border-blue-100">
                            {yearEndEmployeeFeedback[kpi.id] || <span className="text-muted-foreground italic">No comment</span>}
                          </div>
                        ) : (
                          <Textarea
                            placeholder="Add your year-end assessment..."
                            value={yearEndComments[kpi.id] || ''}
                            onChange={(e) => setYearEndComments({ ...yearEndComments, [kpi.id]: e.target.value })}
                            rows={2}
                            className="text-sm"
                          />
                        )}
                      </div>
                      {/* Manager Comment */}
                      <div className="p-3 bg-gray-50/50">
                        <div className="flex items-center gap-2 mb-2">
                          <UserCheck className="h-3.5 w-3.5 text-emerald-600" />
                          <span className="text-xs font-medium text-emerald-700">Manager Comment</span>
                          {yearEndManagerFeedback[kpi.id] ? (
                            <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-500 text-xs ml-auto">Pending</Badge>
                          )}
                        </div>
                        <div className="text-sm text-slate-700 bg-white p-2 rounded border">
                          {yearEndManagerFeedback[kpi.id] || <span className="text-muted-foreground italic">Awaiting manager feedback</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Career Development and Competency Ratings - Only show for Year-End Review */}
      {showYearEnd && (
        <div className="space-y-4">
          {/* Career Development Section */}
          <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
            <h5 className="font-medium text-sm flex items-center gap-2 mb-3 text-indigo-700">
              <TrendingUp className="h-4 w-4" />
              Career Development
              {review?.career_development ? (
                <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted by Manager</Badge>
              ) : (
                <Badge className="bg-gray-100 text-gray-500 text-xs ml-auto">Pending Manager Input</Badge>
              )}
            </h5>
            <div className="text-sm bg-white p-3 rounded border">
              {review?.career_development || (
                <span className="text-muted-foreground italic">Career development notes will be added by your manager</span>
              )}
            </div>
          </div>
          
          {/* Competency Ratings Section (Read-only for Employee) */}
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <h5 className="font-medium text-sm flex items-center gap-2 mb-3 text-amber-700">
              <Star className="h-4 w-4" />
              Competency Ratings
              {review?.competency_ratings?.length > 0 ? (
                <>
                  <Badge className="bg-green-100 text-green-700 text-xs ml-auto">
                    Score: {review.competency_score?.toFixed(1) || '-'}/5
                  </Badge>
                </>
              ) : (
                <Badge className="bg-gray-100 text-gray-500 text-xs ml-auto">Pending Manager Rating</Badge>
              )}
            </h5>
            {review?.competency_ratings?.length > 0 ? (
              <div className="grid gap-2">
                {review.competency_ratings.map((cr, idx) => (
                  <div key={idx} className="bg-white rounded p-3 border flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">{cr.competency_name}</span>
                      {cr.feedback && (
                        <p className="text-xs text-muted-foreground mt-1">{cr.feedback}</p>
                      )}
                    </div>
                    <Badge className={
                      cr.rating >= 4 ? 'bg-green-100 text-green-700' :
                      cr.rating === 3 ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }>
                      {cr.rating}/5
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground bg-white p-3 rounded border italic text-center">
                Competency ratings will be provided by your manager during the year-end review
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Submit Buttons */}
      <div className="flex justify-end gap-3 pt-3">
        {showMidYear && !isMidYearFinal && !hideMidYearSubmit && (
          <Button
            onClick={onSubmitMidYear}
            disabled={submitting || !allMidYearCommentsFilled}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Submit Mid-Year Comments
          </Button>
        )}
        {showYearEnd && !hasYearEndEmployeeSubmitted && (
          <Button 
            onClick={onSubmitYearEnd} 
            disabled={submitting || !allYearEndCommentsFilled} 
            className="bg-purple-600 hover:bg-purple-700"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Submit Year-End Comments
          </Button>
        )}
      </div>
    </div>
  );
};

// Legacy Review Comment Section (keeping for backward compatibility)
const ReviewCommentSection = ({ goals, reviewType, comments, setComments, onSubmit, submitting, review }) => {
  // Check if all goals have employee comments
  const allGoalsHaveComments = goals.every(goal => comments[goal.id]?.trim());
  
  // Get existing employee and manager feedback from review
  const existingFeedback = review?.[reviewType === 'mid_year' ? 'mid_year_feedback' : 'year_end_feedback'] || {};
  const employeeFeedback = existingFeedback.employee_feedback || {};
  const managerFeedback = existingFeedback.manager_feedback || {};
  const hasEmployeeSubmitted = Object.keys(employeeFeedback).length > 0;
  const hasManagerSubmitted = Object.keys(managerFeedback).length > 0;
  
  return (
    <div className="space-y-4 mt-4 p-4 bg-slate-50 rounded-lg border">
      <h4 className="font-semibold flex items-center gap-2 text-lg">
        {reviewType === 'mid_year' ? <Flag className="h-5 w-5 text-amber-600" /> : <ClipboardList className="h-5 w-5 text-purple-600" />}
        {reviewType === 'mid_year' ? 'Mid-Year Review' : 'Year-End Review'}
      </h4>
      
      {/* Employee Comments Section */}
      <div className="bg-white rounded-lg p-4 border">
        <h5 className="font-medium text-sm flex items-center gap-2 mb-3 text-blue-700">
          <Users className="h-4 w-4" />
          Employee Comments
          {hasEmployeeSubmitted && (
            <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted</Badge>
          )}
        </h5>
        
        {goals.map((goal) => (
          <div key={goal.id} className="mb-4 last:mb-0">
            <Label className="text-sm flex items-center gap-2 mb-1">
              {goal.goal_type === 'business' ? <Briefcase className="h-3.5 w-3.5 text-blue-600" /> : <Users className="h-3.5 w-3.5 text-purple-600" />}
              {goal.title} ({goal.weight}%)
            </Label>
            
            {hasEmployeeSubmitted ? (
              // Show submitted employee comments (read-only)
              <div className="text-sm bg-blue-50 p-3 rounded border border-blue-100 text-slate-700">
                {employeeFeedback[goal.id] || <span className="text-muted-foreground italic">No comment provided</span>}
              </div>
            ) : (
              // Show editable textarea
              <Textarea
                placeholder={reviewType === 'mid_year' ? 'Add your mid-year progress update...' : 'Add your year-end assessment...'}
                value={comments[goal.id] || ''}
                onChange={(e) => setComments({ ...comments, [goal.id]: e.target.value })}
                rows={2}
                className="text-sm"
              />
            )}
          </div>
        ))}
        
        {/* Submit Button - Only show if not yet submitted */}
        {!hasEmployeeSubmitted && (
          <div className="flex justify-end mt-4 pt-3 border-t">
            <div className="flex items-center gap-3">
              {!allGoalsHaveComments && (
                <span className="text-xs text-amber-600">Add comments for all goals to submit</span>
              )}
              <Button 
                onClick={onSubmit} 
                disabled={submitting || !allGoalsHaveComments} 
                size="sm"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                Submit Comments
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* Manager Comments Section */}
      <div className="bg-white rounded-lg p-4 border">
        <h5 className="font-medium text-sm flex items-center gap-2 mb-3 text-emerald-700">
          <UserCheck className="h-4 w-4" />
          Manager Comments
          {hasManagerSubmitted ? (
            <Badge className="bg-green-100 text-green-700 text-xs ml-auto">Submitted</Badge>
          ) : (
            <Badge className="bg-gray-100 text-gray-500 text-xs ml-auto">Pending</Badge>
          )}
        </h5>
        
        {hasManagerSubmitted ? (
          goals.map((goal) => (
            <div key={goal.id} className="mb-4 last:mb-0">
              <Label className="text-sm flex items-center gap-2 mb-1">
                {goal.goal_type === 'business' ? <Briefcase className="h-3.5 w-3.5 text-blue-600" /> : <Users className="h-3.5 w-3.5 text-purple-600" />}
                {goal.title} ({goal.weight}%)
                {/* Show rating for Year-End */}
                {reviewType === 'year_end' && goal.rating && (
                  <Badge className={`ml-auto ${
                    goal.rating >= 4 ? 'bg-green-100 text-green-700' :
                    goal.rating === 3 ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    Rating: {goal.rating}/5
                  </Badge>
                )}
              </Label>
              <div className="text-sm bg-emerald-50 p-3 rounded border border-emerald-100 text-slate-700">
                {managerFeedback[goal.id] || <span className="text-muted-foreground italic">No comment provided</span>}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-6 text-muted-foreground text-sm">
            <Clock className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            Manager has not submitted their comments yet
          </div>
        )}
      </div>
    </div>
  );
};

// Scorecard Component
const Scorecard = ({ review }) => {
  if (!review?.calibrated_rating) return null;
  return (
    <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg p-4 border border-primary/20 mt-4">
      <h4 className="font-bold flex items-center gap-2 mb-3">
        <Award className="h-4 w-4 text-primary" /> Final Scorecard
      </h4>
      <div className="grid grid-cols-4 gap-3 text-center">
        <div className="bg-white/50 rounded p-2">
          <p className="text-xs text-muted-foreground">Goal Score</p>
          <p className="text-lg font-bold">{review.goal_score?.toFixed(1) || '-'}</p>
        </div>
        <div className="bg-white/50 rounded p-2">
          <p className="text-xs text-muted-foreground">Competency</p>
          <p className="text-lg font-bold">{review.competency_score?.toFixed(1) || '-'}</p>
        </div>
        <div className="bg-white/50 rounded p-2">
          <p className="text-xs text-muted-foreground">Overall</p>
          <p className="text-lg font-bold">{review.overall_rating?.toFixed(1) || '-'}</p>
        </div>
        <div className="bg-white/50 rounded p-2">
          <p className="text-xs text-muted-foreground">Final</p>
          <div className={`inline-block px-2 py-0.5 rounded-full text-white font-bold ${ratingColors[Math.round(review.calibrated_rating)] || 'bg-primary'}`}>
            {review.calibrated_rating?.toFixed(1)}
          </div>
        </div>
      </div>
    </div>
  );
};

// Rating Label Helper
const getRatingLabel = (rating) => {
  if (!rating) return '-';
  if (rating >= 4.6) return 'Outstanding';
  if (rating >= 3.8) return 'Exceeds Expectation';
  if (rating >= 2.7) return 'Meets Expectation';
  if (rating >= 1.7) return 'Partially Meets';
  return 'Did Not Meet';
};

const getRatingColor = (rating) => {
  if (!rating) return 'bg-gray-100 text-gray-600';
  if (rating >= 4.6) return 'bg-teal-100 text-teal-800';
  if (rating >= 3.8) return 'bg-green-100 text-green-800';
  if (rating >= 2.7) return 'bg-blue-100 text-blue-800';
  if (rating >= 1.7) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
};

// Scorecard Modal Component
const ScorecardModal = ({ open, onClose, review, goals, user }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      // Create printable content and trigger print dialog
      const printContent = document.getElementById('scorecard-print-content');
      if (printContent) {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
          <html>
            <head>
              <title>Performance Scorecard - ${user?.name || 'Employee'}</title>
              <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; background: white; }
                .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #3b82f6; padding-bottom: 20px; }
                .header h1 { color: #1e40af; font-size: 28px; margin-bottom: 5px; }
                .header p { color: #64748b; font-size: 14px; }
                .employee-info { background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); padding: 20px; border-radius: 10px; margin-bottom: 25px; }
                .employee-info h2 { color: #0369a1; margin-bottom: 10px; font-size: 20px; }
                .scores-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 25px; }
                .score-card { background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%); border: 2px solid #e2e8f0; border-radius: 12px; padding: 20px; text-align: center; }
                .score-card.highlight { border-color: #3b82f6; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); }
                .score-card h3 { color: #64748b; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
                .score-card .value { font-size: 32px; font-weight: bold; color: #1e293b; }
                .score-card .label { font-size: 11px; color: #64748b; margin-top: 5px; }
                .section { margin-bottom: 25px; }
                .section h3 { color: #1e40af; font-size: 16px; margin-bottom: 15px; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0; }
                table { width: 100%; border-collapse: collapse; }
                th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
                th { background: #f1f5f9; color: #475569; font-size: 12px; text-transform: uppercase; }
                td { color: #334155; }
                .rating-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-weight: bold; font-size: 12px; }
                .rating-5 { background: #ccfbf1; color: #0f766e; }
                .rating-4 { background: #dcfce7; color: #166534; }
                .rating-3 { background: #dbeafe; color: #1e40af; }
                .rating-2 { background: #fed7aa; color: #c2410c; }
                .rating-1 { background: #fecaca; color: #dc2626; }
                .formula-box { background: #fefce8; border: 1px solid #fde047; border-radius: 10px; padding: 20px; margin-top: 25px; }
                .formula-box h4 { color: #854d0e; margin-bottom: 10px; }
                .formula-box p { color: #713f12; font-size: 13px; line-height: 1.6; }
                .formula { font-family: monospace; background: #fef9c3; padding: 8px 12px; border-radius: 5px; margin: 10px 0; display: inline-block; }
                .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 12px; }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>Performance Scorecard</h1>
                <p>Year ${new Date().getFullYear()} Performance Review</p>
              </div>
              
              <div class="employee-info">
                <h2>${user?.name || 'Employee'}</h2>
                <p>Employee ID: ${user?.employee_number || 'N/A'} | Department: ${user?.department || 'N/A'}</p>
                <p>Review Period: January - December ${new Date().getFullYear()}</p>
              </div>
              
              <div class="scores-grid">
                <div class="score-card">
                  <h3>Goal Score</h3>
                  <div class="value">${review?.goal_score?.toFixed(1) || '-'}</div>
                  <div class="label">80% Weight</div>
                </div>
                <div class="score-card">
                  <h3>Competency</h3>
                  <div class="value">${review?.competency_score?.toFixed(1) || '-'}</div>
                  <div class="label">20% Weight</div>
                </div>
                <div class="score-card">
                  <h3>Overall</h3>
                  <div class="value">${review?.overall_rating?.toFixed(1) || '-'}</div>
                  <div class="label">${getRatingLabel(review?.overall_rating)}</div>
                </div>
                <div class="score-card highlight">
                  <h3>Calibrated</h3>
                  <div class="value">${review?.calibrated_rating?.toFixed(1) || '-'}</div>
                  <div class="label">${review?.calibrated_rating ? getRatingLabel(review?.calibrated_rating) : 'Pending HR'}</div>
                </div>
              </div>
              
              <div class="section">
                <h3>Goals & KPI Ratings</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Goal</th>
                      <th>Type</th>
                      <th>Weight</th>
                      <th>KPIs</th>
                      <th>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${goals?.map(g => `
                      <tr>
                        <td>${g.title}</td>
                        <td>${g.goal_type === 'business' ? 'Business' : 'People'}</td>
                        <td>${g.weight}%</td>
                        <td>${g.kpis?.length || 0} KPIs</td>
                        <td><span class="rating-badge rating-${Math.round(g.rating) || 0}">${g.rating?.toFixed(1) || '-'}/5</span></td>
                      </tr>
                    `).join('') || '<tr><td colspan="5">No goals found</td></tr>'}
                  </tbody>
                </table>
              </div>
              
              <div class="section">
                <h3>Competency Ratings</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Competency</th>
                      <th>Rating</th>
                      <th>Feedback</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${review?.competency_ratings?.length > 0 ? review.competency_ratings.map(comp => `
                      <tr>
                        <td>${comp.competency_name || 'Competency'}</td>
                        <td><span class="rating-badge rating-${Math.round(comp.rating) || 0}">${comp.rating}/5</span></td>
                        <td>${comp.feedback || '-'}</td>
                      </tr>
                    `).join('') : '<tr><td colspan="3">No competency ratings submitted yet</td></tr>'}
                  </tbody>
                </table>
                <p style="margin-top: 10px; font-size: 13px;"><strong>Average Competency Score: ${review?.competency_score?.toFixed(1) || '-'}/5</strong></p>
              </div>
              
              <div class="formula-box">
                <h4>📊 How Scores Are Calculated</h4>
                <p><strong>Goal Score:</strong> Sum of (KPI Weight × KPI Rating) for all KPIs</p>
                <div class="formula">Goal Score = Σ (KPI Weight% × Rating)</div>
                <p><strong>Overall Rating:</strong></p>
                <div class="formula">Overall = (Goal Score × 80%) + (Competency × 20%)</div>
              </div>
              
              <div class="footer">
                <p>Generated on ${new Date().toLocaleDateString()} | Performance Appraisal System</p>
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
      toast.success('PDF ready for download');
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Award className="h-6 w-6 text-primary" />
            Performance Scorecard
          </DialogTitle>
          <DialogDescription>
            {user?.name || 'Employee'} • Year {new Date().getFullYear()} Performance Review
          </DialogDescription>
        </DialogHeader>
        
        <div id="scorecard-print-content" className="space-y-6">
          {/* Scores Overview */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center border border-blue-200">
              <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Goal Score</p>
              <p className="text-2xl font-bold text-blue-900">{review?.goal_score?.toFixed(1) || '-'}</p>
              <p className="text-xs text-blue-600 mt-1">80% Weight</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center border border-purple-200">
              <p className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-1">Competency</p>
              <p className="text-2xl font-bold text-purple-900">{review?.competency_score?.toFixed(1) || '-'}</p>
              <p className="text-xs text-purple-600 mt-1">20% Weight</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 text-center border border-emerald-200">
              <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Overall</p>
              <p className="text-2xl font-bold text-emerald-900">{review?.overall_rating?.toFixed(1) || '-'}</p>
              <Badge className={`mt-1 text-xs ${getRatingColor(review?.overall_rating)}`}>
                {getRatingLabel(review?.overall_rating)}
              </Badge>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 text-center border border-amber-200">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-1">Calibrated</p>
              <p className="text-2xl font-bold text-amber-900">{review?.calibrated_rating?.toFixed(1) || '-'}</p>
              {review?.calibrated_rating ? (
                <Badge className={`mt-1 text-xs ${getRatingColor(review?.calibrated_rating)}`}>
                  {getRatingLabel(review?.calibrated_rating)}
                </Badge>
              ) : (
                <p className="text-xs text-amber-600 mt-1">Pending HR</p>
              )}
            </div>
          </div>
          
          {/* Goals Breakdown */}
          <div className="bg-slate-50 rounded-xl p-4 border">
            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <Target className="h-4 w-4 text-blue-600" />
              Goals & KPI Performance
            </h4>
            <div className="space-y-3">
              {goals?.filter(g => g.status === 'approved').map((goal) => (
                <div key={goal.id} className="bg-white rounded-lg p-3 border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {goal.goal_type === 'business' ? 
                        <Briefcase className="h-4 w-4 text-blue-600" /> : 
                        <Users className="h-4 w-4 text-purple-600" />
                      }
                      <span className="font-medium text-sm">{goal.title}</span>
                      <Badge variant="outline" className="text-xs">{goal.weight}%</Badge>
                    </div>
                    <Badge className={`${
                      goal.rating >= 4 ? 'bg-green-100 text-green-700' :
                      goal.rating >= 3 ? 'bg-blue-100 text-blue-700' :
                      goal.rating >= 1 ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {goal.rating?.toFixed(1) || '-'}/5
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {goal.kpis?.length || 0} KPIs • {goal.goal_type === 'business' ? 'Business Goal' : 'People Goal'}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Competency Ratings Section */}
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <Star className="h-4 w-4 text-purple-600" />
              Competency Ratings
              {review?.competency_score && (
                <Badge className="ml-auto bg-purple-100 text-purple-700">
                  Avg: {review.competency_score.toFixed(1)}/5
                </Badge>
              )}
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {review?.competency_ratings?.length > 0 ? (
                review.competency_ratings.map((comp, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{comp.competency_name || `Competency ${index + 1}`}</span>
                      <Badge className={`${
                        comp.rating >= 4 ? 'bg-green-100 text-green-700' :
                        comp.rating === 3 ? 'bg-blue-100 text-blue-700' :
                        comp.rating >= 1 ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {comp.rating}/5
                      </Badge>
                    </div>
                    {comp.feedback && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">{comp.feedback}</p>
                    )}
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center py-4 text-muted-foreground text-sm">
                  No competency ratings submitted yet
                </div>
              )}
            </div>
          </div>
          
          {/* Rating Scale Reference */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-4 border">
            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <BarChart3 className="h-4 w-4 text-slate-600" />
              Rating Scale Reference
            </h4>
            <div className="grid grid-cols-5 gap-2 text-center text-xs">
              <div className="bg-teal-100 text-teal-800 rounded-lg p-2">
                <div className="font-bold">5</div>
                <div>Outstanding</div>
                <div className="text-teal-600">(4.6 - 5.0)</div>
              </div>
              <div className="bg-green-100 text-green-800 rounded-lg p-2">
                <div className="font-bold">4</div>
                <div>Exceeds</div>
                <div className="text-green-600">(3.8 - 4.5)</div>
              </div>
              <div className="bg-blue-100 text-blue-800 rounded-lg p-2">
                <div className="font-bold">3</div>
                <div>Meets</div>
                <div className="text-blue-600">(2.7 - 3.7)</div>
              </div>
              <div className="bg-orange-100 text-orange-800 rounded-lg p-2">
                <div className="font-bold">2</div>
                <div>Partial</div>
                <div className="text-orange-600">(1.7 - 2.6)</div>
              </div>
              <div className="bg-red-100 text-red-800 rounded-lg p-2">
                <div className="font-bold">1</div>
                <div>Did Not Meet</div>
                <div className="text-red-600">(1.0 - 1.6)</div>
              </div>
            </div>
          </div>
          
          {/* How Scores Are Calculated */}
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-amber-800">
              <Info className="h-4 w-4" />
              How Scores Are Calculated
            </h4>
            <div className="space-y-3 text-sm text-amber-900">
              <div>
                <p className="font-medium">Goal Score (80% of Overall)</p>
                <p className="text-xs mt-1 bg-amber-100 p-2 rounded font-mono">
                  Goal Score = Σ (KPI Weight% × KPI Rating)
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Each KPI&apos;s weight is multiplied by its rating, then summed for all KPIs.
                </p>
              </div>
              <div>
                <p className="font-medium">Overall Rating</p>
                <p className="text-xs mt-1 bg-amber-100 p-2 rounded font-mono">
                  Overall = (Goal Score × 80%) + (Competency Score × 20%)
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Combines goal performance with competency assessment.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleDownloadPDF} disabled={downloading}>
            {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Download PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
export default function GoalsPage() {
  const { user } = useAuth();
  const { performanceCycle } = usePerformanceCycle();
  
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [goals, setGoals] = useState([]);
  const [stats, setStats] = useState(null);
  const [review, setReview] = useState(null);
  const [myTemplate, setMyTemplate] = useState(null);
  const [preApprovedGoals, setPreApprovedGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearCycleData, setYearCycleData] = useState(null);
  const [allCycles, setAllCycles] = useState([]);
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [addingGoalType, setAddingGoalType] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Review states
  const [showMidYearReview, setShowMidYearReview] = useState(false);
  const [showYearEndReview, setShowYearEndReview] = useState(false);
  const [midYearComments, setMidYearComments] = useState({});
  const [yearEndComments, setYearEndComments] = useState({});
  const [showScorecardModal, setShowScorecardModal] = useState(false);
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [signingAs, setSigningAs] = useState(null); // 'employee' or 'manager'
  const [signatureName, setSignatureName] = useState('');
  const [signingLoading, setSigningLoading] = useState(false);

  // Mid-Year Review workflow states
  const [midYearReviewStatus, setMidYearReviewStatus] = useState('not_started'); // 'not_started' | 'in_progress' | 'completed' | 'submitted'
  const [midYearReviewSubmitting, setMidYearReviewSubmitting] = useState(false);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  
  // Available years starting from current year
  const availableYears = useMemo(() => [currentYear, currentYear + 1, currentYear + 2, currentYear + 3], [currentYear]);

  // Goal categorization
  const businessGoals = goals.filter(g => g.goal_type === 'business');
  const peopleGoals = goals.filter(g => g.goal_type === 'people');
  const businessWeight = businessGoals.reduce((sum, g) => sum + (g.weight || 0), 0);
  const peopleWeight = peopleGoals.reduce((sum, g) => sum + (g.weight || 0), 0);
  
  // Status checks
  const allApproved = goals.length > 0 && goals.every(g => g.status === 'approved');
  const anyRejected = goals.some(needsRevision);
  const anyPending = goals.some(g => g.status === 'pending_approval');
  
  // Requirements check
  const businessCountMet = businessGoals.length >= 3 && businessGoals.length <= 5;
  const businessWeightMet = businessWeight === 90;
  const peopleCountMet = peopleGoals.length >= 1;
  const peopleWeightMet = peopleWeight === 10;
  const allRequirementsMet = businessCountMet && businessWeightMet && peopleCountMet && peopleWeightMet;
  
  // Stage checks
  const isGoalSettingActive = yearCycleData?.stages?.goal_setting?.is_active;
  const isGoalSettingEnabled = yearCycleData?.goal_setting_enabled !== false;
  const isMidYearActive = yearCycleData?.stages?.mid_year?.is_active;
  const isMidYearEnabled = yearCycleData?.mid_year_enabled !== false;
  const isYearEndActive = yearCycleData?.stages?.year_end?.is_active;
  const isYearEndEnabled = yearCycleData?.year_end_enabled !== false;
  const isYearActive = yearCycleData?.is_year_active;
  
  // Permissions - also check if year is active AND stage is enabled
  const canAddGoals = isYearActive && isGoalSettingEnabled && isGoalSettingActive && !allApproved && !anyPending;
  const canEditGoals = canAddGoals || anyRejected;
  const canSubmitGoals = isGoalSettingEnabled && allRequirementsMet && goals.some(g => g.status === 'draft' || g.status === 'rejected');

  // Gates the mid-year Submit button below, the same way the manager's Submit is gated
  // in TeamGoalsPage. Previously the in-progress flow's button was always enabled and
  // only failed with a toast on click.
  const allMidYearCommentsFilled = hasAllKpiComments(goals, midYearComments);

  // Overall status
  const getOverallStatus = () => {
    if (!isYearActive) return { label: 'Year Locked', color: 'bg-gray-100 text-gray-600' };
    if (goals.length === 0) return { label: 'No Goals', color: 'bg-gray-100 text-gray-600' };
    if (anyRejected) return { label: 'Rejected', color: 'bg-red-100 text-red-700' };
    if (anyPending) return { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700' };
    if (allApproved) return { label: 'Approved', color: 'bg-emerald-100 text-emerald-700' };
    return { label: 'Draft', color: 'bg-blue-100 text-blue-700' };
  };
  const overallStatus = getOverallStatus();

  // Helper: Get stage status and info
  const getStageStatus = (stageName) => {
    const stage = yearCycleData?.stages?.[stageName];
    if (!stage) return { status: 'Not Configured', color: 'bg-gray-100 text-gray-500', startDate: null };
    
    const now = new Date();
    const startDate = stage.start_date ? new Date(stage.start_date) : null;
    const endDate = stage.end_date ? new Date(stage.end_date) : null;
    
    // Check if employee has submitted
    const feedbackData = stageName === 'mid_year'
      ? review?.mid_year_feedback
      : review?.year_end_feedback;

    const hasEmployeeSubmitted = feedbackData?.employee_feedback && Object.keys(feedbackData.employee_feedback).length > 0;
    // A manager DRAFT must not count as submitted — the employee shouldn't see the review as complete.
    const hasManagerFeedback = feedbackData?.manager_feedback && Object.keys(feedbackData.manager_feedback).length > 0;
    const hasManagerSubmitted = hasManagerFeedback && feedbackData?.manager_submission_status !== 'draft';
    // Default to 'submitted' for backward compatibility with old reviews
    const submissionStatus = feedbackData?.submission_status || (hasEmployeeSubmitted ? 'submitted' : undefined);

    if (!stage.is_active && startDate && now < startDate) {
      const day = startDate.getDate().toString().padStart(2, '0');
      const month = startDate.toLocaleDateString('en-US', { month: 'short' });
      const year = startDate.getFullYear();
      return {
        status: 'Not Due',
        color: 'bg-gray-100 text-gray-500',
        startDate: `${day}-${month}-${year}`
      };
    }

    if (hasManagerSubmitted && hasEmployeeSubmitted) {
      return { status: 'Completed', color: 'bg-green-100 text-green-700', startDate: null };
    }

    // Show draft status if employee saved progress but hasn't finally submitted (mid-year workflow)
    if (hasEmployeeSubmitted && submissionStatus === 'draft' && !hasManagerSubmitted) {
      const draftLabel = stageName === 'mid_year' ? 'MidYear Draft' : 'Draft';
      return { status: draftLabel, color: 'bg-yellow-100 text-yellow-700', startDate: null };
    }

    if (hasEmployeeSubmitted && !hasManagerSubmitted) {
      return { status: 'Pending Manager', color: 'bg-amber-100 text-amber-700', startDate: null };
    }

    if (stage.is_active && !hasEmployeeSubmitted) {
      return { status: 'Pending Submission', color: 'bg-blue-100 text-blue-700', startDate: null };
    }
    
    if (!stage.is_active && endDate && now > endDate) {
      return { status: 'Closed', color: 'bg-gray-100 text-gray-500', startDate: null };
    }
    
    return { status: 'Not Started', color: 'bg-gray-100 text-gray-500', startDate: null };
  };

  // Calibration stage status
  const getCalibrationStatus = () => {
    const stage = yearCycleData?.stages?.calibration;
    if (!stage) return { status: 'Not Configured', color: 'bg-gray-100 text-gray-500', startDate: null };
    
    const now = new Date();
    const startDate = stage.start_date ? new Date(stage.start_date) : null;
    
    if (review?.calibrated_rating) {
      return { status: 'Completed', color: 'bg-green-100 text-green-700', startDate: null };
    }
    
    if (!stage.is_active && startDate && now < startDate) {
      const day = startDate.getDate().toString().padStart(2, '0');
      const month = startDate.toLocaleDateString('en-US', { month: 'short' });
      const year = startDate.getFullYear();
      return { 
        status: 'Not Due', 
        color: 'bg-gray-100 text-gray-500', 
        startDate: `${day}-${month}-${year}`
      };
    }
    
    if (stage.is_active) {
      return { status: 'In Progress', color: 'bg-blue-100 text-blue-700', startDate: null };
    }
    
    return { status: 'Pending', color: 'bg-gray-100 text-gray-500', startDate: null };
  };

  // Load all cycles on mount
  useEffect(() => {
    loadAllCycles();
  }, []);

  // user?.id is a dependency because logging in does NOT remount this page — login()
  // is only a state update, so a second account signing in on top of a live session
  // leaves this component mounted. Without it, `goals` (and the KPI ids the review
  // form is keyed by) stay on the PREVIOUS person while requests carry the new token.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [selectedYear, user?.id]);

  const loadAllCycles = async () => {
    try {
      const cyclesRes = await performanceCycleApi.getAll();
      setAllCycles(cyclesRes.data);
      
      // Find first active year to select
      const activeYear = cyclesRes.data.find(c => c.is_year_active);
      if (activeYear) {
        setSelectedYear(activeYear.year);
      }
    } catch (error) {
      console.error('Failed to load cycles:', error);
    }
  };
  
  const loadData = async () => {
    setLoading(true);
    try {
      // For pre-approved users, auto-create any missing goal records from the template
      // before loading goals so they're included in the getAll() response.
      if (user?.pre_approved_goals) {
        try {
          await goalLibraryApi.materializeGoals(selectedYear);
        } catch (err) {
          console.error('Failed to materialize pre-approved goals', err);
        }
      }

      const [goalsRes, statsRes, reviewsRes, cycleRes] = await Promise.all([
        goalsApi.getAll({ year: selectedYear }),
        goalsApi.getStats(selectedYear),
        reviewsApi.getAll({ year: selectedYear }),
        performanceCycleApi.get(selectedYear),
      ]);
      setGoals(goalsRes.data);
      setStats(statsRes.data);
      const reviewData = reviewsRes.data?.[0] || null;
      setReview(reviewData);
      setYearCycleData(cycleRes.data);

      // Initialize mid-year review status based on submission state
      const midYearEmpFb = reviewData?.mid_year_feedback?.employee_feedback;
      const midYearSubStatus = reviewData?.mid_year_feedback?.submission_status;
      if (midYearEmpFb && Object.keys(midYearEmpFb).length > 0) {
        // Pre-fill local comments from the saved draft/submission so the user can resume or view
        setMidYearComments(midYearEmpFb);
        if (midYearSubStatus === 'draft') {
          // Draft: keep as 'not_started' so the "Start Midyear Review" button shows and the user can resume editing
          setMidYearReviewStatus('not_started');
        } else {
          // Final submission (or legacy feedback with no submission_status)
          setMidYearReviewStatus('submitted');
        }
      } else {
        // No saved feedback for THIS user/year — clear any comments left over from a
        // previously loaded user or year rather than letting them sit in the form.
        setMidYearComments({});
        setMidYearReviewStatus('not_started');
      }

      // Load pre-approved templates if applicable
      if (user?.pre_approved_goals) {
        try {
          const tmplRes = await goalLibraryApi.getMyTemplates();
          const templates = tmplRes.data || [];
          setMyTemplate(templates[0] || null);
          setPreApprovedGoals((goalsRes.data || []).filter(g => g.goal_type === 'pre_approved'));
          if (templates.length > 0) setIsExpanded(true);
        } catch (err) {
          console.error('Failed to load pre-approved templates', err);
        }
      }

      // Auto expand if there are goals
      if (goalsRes.data.length > 0) setIsExpanded(true);
    } catch (error) {
      console.error('Failed to load goals:', error);
      toast.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoal = async (goalData) => {
    try {
      const data = {
        title: goalData.title,
        goal_type: goalData.goal_type,
        year: selectedYear,
        kpis: goalData.kpis.map(kpi => ({
          name: kpi.name,
          measurement: kpi.measurement,
          timeline: kpi.timeline,
          weight: parseFloat(kpi.weight),
        })),
      };

      if (editingGoal) {
        await goalsApi.update(editingGoal.id, data);
        toast.success('Goal updated');
      } else {
        await goalsApi.create(data);
        toast.success('Goal added');
      }
      
      setAddingGoalType(null);
      setEditingGoal(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save goal');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!confirm('Delete this goal?')) return;
    try {
      await goalsApi.delete(goalId);
      toast.success('Goal deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const handleSubmitGoals = async () => {
    setSubmitting(true);
    try {
      await goalsApi.submit(selectedYear);
      toast.success('Goals submitted for approval');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit goals');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReview = async (type) => {
    setSubmitting(true);
    try {
      const comments = type === 'mid_year' ? midYearComments : yearEndComments;
      
      // Submit employee feedback via new API
      await reviewsApi.submitEmployeeFeedback(selectedYear, type, comments);
      
      toast.success(`${type === 'mid_year' ? 'Mid-Year' : 'Year-End'} comments submitted`);
      type === 'mid_year' ? setShowMidYearReview(false) : setShowYearEndReview(false);
      loadData();
    } catch (error) {
      console.error('Failed to submit comments:', error);
      toast.error('Failed to submit comments');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle signing calibration review
  const handleSignCalibration = async () => {
    if (!review || !signatureName.trim()) {
      toast.error('Please enter your name to sign');
      return;
    }
    
    setSigningLoading(true);
    try {
      await reviewsApi.sign(review.id, {
        type: signingAs,
        signature: signatureName,
      });
      toast.success('Signature recorded successfully');
      setSigningAs(null);
      setSignatureName('');
      loadData();
    } catch (error) {
      console.error('Failed to sign:', error);
      toast.error('Failed to record signature');
    } finally {
      setSigningLoading(false);
    }
  };

  const handleStartMidYearReview = () => {
    console.log('Starting midyear review with saved comments:', midYearComments);
    setMidYearReviewStatus('in_progress');
    setShowMidYearReview(true);
  };

  const handleLeaveMidYearReview = async () => {
    // Check if ALL comments are filled
    const allCommentsFilled = allMidYearCommentsFilled;

    // Always save to API (both partial and complete)
    let saved = false;
    if (Object.keys(midYearComments).length > 0) {
      try {
        // "Leave Review" always saves as a DRAFT (stays with employee, never auto-sent to manager)
        await reviewsApi.submitEmployeeFeedback(selectedYear, 'mid_year', midYearComments, 'draft');
        saved = true;
        if (allCommentsFilled) {
          toast.success('Progress saved as draft. Click "Start Midyear Review" to review and submit.');
        } else {
          toast.success('Draft saved. Click "Start Midyear Review" to continue where you left off.');
        }
      } catch (error) {
        console.error('Failed to save draft:', error);
        toast.warning('Review paused (comments saved locally). Network error saving to server.');
      }
    } else {
      toast.info('Review paused. Click "Start Midyear Review" to resume.');
    }

    setShowMidYearReview(false);
    // Reload so the review state (and the "MidYear Draft" badge) reflects the saved draft.
    // loadData() re-derives midYearReviewStatus and re-fills midYearComments from the server.
    if (saved) {
      await loadData();
    } else {
      setMidYearReviewStatus('not_started');
    }
  };

  const handleShowSubmitConfirmation = () => {
    // Belt-and-braces: the Submit button is already disabled when this is false, but the
    // check stays so the confirmation dialog can never open on an incomplete review.
    if (!allMidYearCommentsFilled) {
      toast.error('Please fill in comments for all KPIs before completing');
      return;
    }

    setShowSubmitConfirmation(true);
  };

  const handleCompleteMidYearReview = async () => {
    setMidYearReviewSubmitting(true);
    try {
      // Explicit final submission → moves to manager review
      await reviewsApi.submitEmployeeFeedback(selectedYear, 'mid_year', midYearComments, 'submitted');
      setMidYearReviewStatus('submitted');
      setMidYearComments({});
      setShowSubmitConfirmation(false);
      toast.success('Mid-Year Review submitted successfully');
    } catch (error) {
      console.error('Failed to submit review:', error);
      toast.error('Failed to submit review');
    } finally {
      setMidYearReviewSubmitting(false);
    }
  };

  const handleAddGoalsClick = () => {
    setIsExpanded(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="goals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My Goals</h1>
          <p className="text-sm text-muted-foreground">Track your performance goals and reviews</p>
        </div>
        <YearSelector selectedYear={selectedYear} onYearChange={setSelectedYear} availableYears={availableYears} cyclesData={allCycles} />
      </div>

      {/* Stage Disabled Alerts */}
      {!isGoalSettingEnabled && (
        <Alert variant="destructive" className="bg-red-50 border-red-200">
          <Lock className="h-4 w-4" />
          <AlertDescription className="ml-2">
            <span className="font-semibold">Goal Setting stage is closed.</span> You cannot add, edit, or submit goals at this time. Please contact your administrator for more information.
          </AlertDescription>
        </Alert>
      )}
      {!isMidYearEnabled && isGoalSettingEnabled && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="ml-2 text-amber-800">
            <span className="font-semibold">Mid-Year Review stage is closed.</span> Mid-year self-assessments are currently disabled.
          </AlertDescription>
        </Alert>
      )}
      {!isYearEndEnabled && isGoalSettingEnabled && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="ml-2 text-amber-800">
            <span className="font-semibold">Year-End Review stage is closed.</span> Year-end self-assessments are currently disabled.
          </AlertDescription>
        </Alert>
      )}

      {/* Manager rejected mid-year/year-end comments — sent back to draft for revision, mirrors goal rejection banner */}
      {review?.mid_year_feedback?.submission_status !== 'submitted' && review?.mid_year_feedback?.rejection_feedback && (
        <Alert variant="destructive" className="bg-red-50 border-red-200">
          <XCircle className="h-4 w-4" />
          <AlertDescription className="ml-2">
            <span className="font-semibold">Your mid-year review comments were sent back for revision.</span>
            <p className="mt-1">{review.mid_year_feedback.rejection_feedback}</p>
          </AlertDescription>
        </Alert>
      )}
      {review?.year_end_feedback?.submission_status !== 'submitted' && review?.year_end_feedback?.rejection_feedback && (
        <Alert variant="destructive" className="bg-red-50 border-red-200">
          <XCircle className="h-4 w-4" />
          <AlertDescription className="ml-2">
            <span className="font-semibold">Your year-end review comments were sent back for revision.</span>
            <p className="mt-1">{review.year_end_feedback.rejection_feedback}</p>
          </AlertDescription>
        </Alert>
      )}

      {/* SMART Objectives Reference */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">SMART Objectives (KPI's) - Goal Setting</CardTitle>
          <CardDescription>Guidelines for setting effective performance objectives</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3">
            {[
              { 
                letter: 'S', 
                label: 'Specific', 
                color: 'bg-blue-600',
                borderColor: 'border-blue-600',
                bgLight: 'bg-blue-50',
                description: 'Objectives should be straightforward and emphasize the business result that you would like to achieve.'
              },
              { 
                letter: 'M', 
                label: 'Measurable', 
                color: 'bg-green-600',
                borderColor: 'border-green-600',
                bgLight: 'bg-green-50',
                description: 'Establish concrete criteria for measuring progress toward the attainment of each objective that you set.'
              },
              { 
                letter: 'A', 
                label: 'Achievable', 
                color: 'bg-amber-500',
                borderColor: 'border-amber-500',
                bgLight: 'bg-amber-50',
                description: 'Objectives must be within your capacity to reach. You cannot commit to accomplishing objectives that are far too out of your reach, or unreasonable.'
              },
              { 
                letter: 'R', 
                label: 'Relevant', 
                color: 'bg-purple-600',
                borderColor: 'border-purple-600',
                bgLight: 'bg-purple-50',
                description: 'Make sure each objective is linked with the department KPIs and fits with your immediate and long-range plans.'
              },
              { 
                letter: 'T', 
                label: 'Time Bound', 
                color: 'bg-red-500',
                borderColor: 'border-red-500',
                bgLight: 'bg-red-50',
                description: 'Setting an end point on your objectives gives you a clear target to work towards.'
              },
            ].map((item) => (
              <div key={item.letter} className={`rounded-lg border-2 ${item.borderColor} ${item.bgLight} p-3 text-center`}>
                <div className={`w-10 h-10 rounded-full ${item.color} flex items-center justify-center text-white font-bold text-lg mx-auto mb-2`}>
                  {item.letter}
                </div>
                <h4 className="font-semibold text-sm mb-2">{item.label}</h4>
                <p className="text-xs text-muted-foreground leading-tight">{item.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Main Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold flex-shrink-0">
                {selectedYear}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-xl">{selectedYear} Goals</CardTitle>
                  <Badge className={overallStatus.color}>{overallStatus.label}</Badge>
                </div>
                <CardDescription>
                  {user?.pre_approved_goals
                    ? `${preApprovedGoals.length} pre-approved goal${preApprovedGoals.length !== 1 ? 's' : ''}`
                    : goals.length === 0 ? 'No goals defined' : `${businessGoals.length} Business • ${peopleGoals.length} People • ${businessWeight + peopleWeight}% Weight`}
                </CardDescription>
              </div>
            </div>
            
            <div className="flex flex-wrap items-start gap-3">
              {/* Submit Button — not shown for pre-approved track (auto-submitted on materialization) */}
              {!user?.pre_approved_goals && goals.length > 0 && !allApproved && !anyPending && (
                <Button
                  onClick={handleSubmitGoals}
                  disabled={!canSubmitGoals || submitting}
                  size="sm"
                  className={!canSubmitGoals ? 'opacity-50' : ''}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : canSubmitGoals ? (
                    <Send className="h-4 w-4 mr-1" />
                  ) : (
                    <Lock className="h-4 w-4 mr-1" />
                  )}
                  Submit Goals
                </Button>
              )}
              
              {/* Add Goals Button - Show prominently when no goals (not for pre-approved track) */}
              {!user?.pre_approved_goals && goals.length === 0 && canAddGoals && (
                <Button size="sm" onClick={handleAddGoalsClick} className="whitespace-nowrap">
                  <Plus className="h-4 w-4 mr-1" /> Add Goals
                </Button>
              )}
              
              {/* Mid-Year Review Button with Status or Start Midyear Review */}
              <div className="flex flex-col items-center">
                {allApproved && isMidYearEnabled && isMidYearActive && midYearReviewStatus === 'not_started' ? (
                  <Button
                    size="sm"
                    onClick={handleStartMidYearReview}
                    className="whitespace-nowrap bg-amber-600 hover:bg-amber-700"
                  >
                    <Flag className="h-4 w-4 mr-1" />
                    Start Midyear Review
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMidYearReview(!showMidYearReview)}
                    disabled={!isMidYearEnabled || !isMidYearActive || !allApproved}
                    className={`relative whitespace-nowrap ${(!isMidYearEnabled || !isMidYearActive || !allApproved) ? 'opacity-50' : ''}`}
                  >
                    {(!isMidYearEnabled || !isMidYearActive || !allApproved) ? <Lock className="h-4 w-4 mr-1" /> : <Flag className="h-4 w-4 mr-1" />}
                    Mid-Year Review
                    <Badge className={`ml-2 text-xs ${getStageStatus('mid_year').color}`}>
                      {getStageStatus('mid_year').status}
                    </Badge>
                  </Button>
                )}
                {getStageStatus('mid_year').startDate && (
                  <span className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <CalendarIcon className="h-3 w-3" />
                    Starts {getStageStatus('mid_year').startDate}
                  </span>
                )}
              </div>
              
              {/* Year-End Review Button with Status */}
              <div className="flex flex-col items-center">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowYearEndReview(!showYearEndReview)}
                  disabled={!isYearEndEnabled || !isYearEndActive || !allApproved}
                  className={`relative whitespace-nowrap ${(!isYearEndEnabled || !isYearEndActive || !allApproved) ? 'opacity-50' : ''}`}
                >
                  {(!isYearEndEnabled || !isYearEndActive || !allApproved) ? <Lock className="h-4 w-4 mr-1" /> : <ClipboardList className="h-4 w-4 mr-1" />}
                  Year-End Review
                  <Badge className={`ml-2 text-xs ${getStageStatus('year_end').color}`}>
                    {getStageStatus('year_end').status}
                  </Badge>
                </Button>
                {getStageStatus('year_end').startDate && (
                  <span className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <CalendarIcon className="h-3 w-3" />
                    Starts {getStageStatus('year_end').startDate}
                  </span>
                )}
              </div>
              
              {/* Calibration Button - Shows calibration details when clicked */}
              <div className="flex flex-col items-center">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowCalibrationModal(true)}
                  disabled={!review?.goal_score}
                  className={`whitespace-nowrap ${review?.goal_score ? '' : 'opacity-50'}`}
                >
                  <Star className="h-4 w-4 mr-1" />
                  Calibration
                  <Badge className={`ml-2 text-xs ${getCalibrationStatus().color}`}>
                    {getCalibrationStatus().status}
                  </Badge>
                </Button>
                {getCalibrationStatus().startDate && (
                  <span className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <CalendarIcon className="h-3 w-3" />
                    Starts {getCalibrationStatus().startDate}
                  </span>
                )}
              </div>
              
              {/* Scorecard Button */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowScorecardModal(true)}
                disabled={!review?.goal_score && !review?.competency_score && !review?.overall_rating}
                className={`whitespace-nowrap ${(review?.goal_score || review?.competency_score || review?.overall_rating) ? 'border-primary text-primary hover:bg-primary/10' : 'opacity-50'}`}
              >
                <Award className="h-4 w-4 mr-1" />
                Scorecard
              </Button>
              
              {/* Expand Button */}
              {(goals.length > 0 || myTemplate !== null) && (
                <Button variant="ghost" size="sm" onClick={() => setIsExpanded(!isExpanded)} className="h-8 w-8 p-0 flex-shrink-0">
                  {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <Collapsible open={isExpanded}>
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4">
              {/* Year Locked Notice */}
              {!isYearActive && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <Lock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-600">This year is locked</p>
                  <p className="text-xs text-gray-500 mt-1">Contact HR/Admin to enable this year in Performance Cycle Management</p>
                </div>
              )}
              
              {/* Rejection Notice with Feedback */}
              {isYearActive && anyRejected && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Goals rejected by manager. Please review feedback, make changes, and resubmit.</p>
                      {/* Show the manager's rejection feedback */}
                      {goals.find(g => needsRevision(g) && g.manager_feedback)?.manager_feedback && (
                        <div className="mt-2 p-2 bg-red-100 rounded text-sm text-red-700">
                          <strong>Manager&apos;s Feedback:</strong> {goals.find(g => needsRevision(g) && g.manager_feedback)?.manager_feedback}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Frozen Notice */}
              {isYearActive && allApproved && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-emerald-600" />
                    <p className="text-sm font-medium text-emerald-800">Goals approved and locked. No further edits allowed.</p>
                  </div>
                </div>
              )}
              
              {/* Requirements Section — not applicable for pre-approved track */}
              {!user?.pre_approved_goals && isYearActive && canAddGoals && !allApproved && (
                <div className="bg-slate-50 rounded-lg p-4 border">
                  <h4 className="font-semibold text-sm mb-3">Goal Requirements</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <RequirementCheck 
                      met={businessCountMet} 
                      label={`Business Goals: ${businessGoals.length}/3-5`}
                      helperText="Should be Minimum 3 and Maximum 5"
                    />
                    <RequirementCheck 
                      met={businessWeightMet} 
                      label={`Business Weight: ${businessWeight}%/90%`}
                      helperText="Total should be 90%"
                    />
                    <RequirementCheck 
                      met={peopleCountMet} 
                      label={`People Goals: ${peopleGoals.length}/1+`}
                      helperText="Should be minimum 1"
                    />
                    <RequirementCheck 
                      met={peopleWeightMet} 
                      label={`People Weight: ${peopleWeight}%/10%`}
                      helperText="Total should be 10%"
                    />
                  </div>
                  {allRequirementsMet && (
                    <div className="mt-3 flex items-center gap-2 text-emerald-700 text-sm font-medium">
                      <Check className="h-4 w-4" /> All requirements met! Ready to submit.
                    </div>
                  )}
                </div>
              )}

              {/* Goals Section - only show if year is active AND not reviewing */}
              {isYearActive && midYearReviewStatus !== 'in_progress' && (
                user?.pre_approved_goals ? (
                  myTemplate ? (
                    (myTemplate.sections || []).map((section, sectionIdx) => (
                      <div key={section.id}>
                        {sectionIdx > 0 && <Separator />}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Layers className="h-4 w-4 text-emerald-600" />
                            <h3 className="font-semibold text-sm">{section.name}</h3>
                            <Badge variant="secondary" className="text-xs">
                              {(section.goals || []).filter(lg => preApprovedGoals.some(g => g.library_goal_id === lg.id)).length}/{section.goals?.length || 0}
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            {(section.goals || []).map((libraryGoal) => {
                              const savedGoal = preApprovedGoals.find(g => g.library_goal_id === libraryGoal.id) || null;
                              if (savedGoal) {
                                return (
                                  <GoalCard
                                    key={libraryGoal.id}
                                    goal={savedGoal}
                                    canEdit={false}
                                  />
                                );
                              }
                              // Fallback for goals not yet materialized (e.g. year inactive)
                              return (
                                <div key={libraryGoal.id} className="p-3 rounded-lg border bg-slate-50 text-sm text-slate-500 flex items-center gap-2">
                                  <Target className="h-4 w-4" />
                                  {libraryGoal.title}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed text-sm text-muted-foreground">
                      No goal template assigned. Please contact your administrator.
                    </div>
                  )
                ) : (
                <>
                <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-blue-600" />
                    Business Goals ({businessGoals.length}/3-5) • {businessWeight}%/90%
                  </h3>
                  {canEditGoals && businessGoals.length < 5 && (
                    <Button variant="outline" size="sm" onClick={() => { setAddingGoalType('business'); setEditingGoal(null); }} className="h-7 text-xs">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add
                    </Button>
                  )}
                </div>

                {addingGoalType === 'business' && !editingGoal && (
                  <InlineGoalForm
                    goalType="business"
                    onSave={handleSaveGoal}
                    onCancel={() => setAddingGoalType(null)}
                  />
                )}

                {editingGoal?.goal_type === 'business' && (
                  <InlineGoalForm
                    goalType="business"
                    editingGoal={editingGoal}
                    onSave={handleSaveGoal}
                    onCancel={() => setEditingGoal(null)}
                  />
                )}

                {businessGoals.length === 0 && addingGoalType !== 'business' ? (
                  <div className="text-center py-4 bg-slate-50 rounded-lg border border-dashed text-sm text-muted-foreground">
                    No business goals yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {businessGoals.map((goal) => (
                      editingGoal?.id !== goal.id && (
                        <GoalCard
                          key={goal.id}
                          goal={goal}
                          onEdit={(g) => { setEditingGoal(g); setAddingGoalType(null); }}
                          onDelete={handleDeleteGoal}
                          canEdit={canEditGoals && (goal.status === 'draft' || goal.status === 'rejected')}
                        />
                      )
                    ))}
                  </div>
                )}
              </div>

              <Separator />

              {/* People Goals Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Users className="h-4 w-4 text-purple-600" />
                    People Goals ({peopleGoals.length}/1+) • {peopleWeight}%/10%
                  </h3>
                  {canEditGoals && (
                    <Button variant="outline" size="sm" onClick={() => { setAddingGoalType('people'); setEditingGoal(null); }} className="h-7 text-xs">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add
                    </Button>
                  )}
                </div>

                {addingGoalType === 'people' && !editingGoal && (
                  <InlineGoalForm
                    goalType="people"
                    onSave={handleSaveGoal}
                    onCancel={() => setAddingGoalType(null)}
                  />
                )}

                {editingGoal?.goal_type === 'people' && (
                  <InlineGoalForm
                    goalType="people"
                    editingGoal={editingGoal}
                    onSave={handleSaveGoal}
                    onCancel={() => setEditingGoal(null)}
                  />
                )}

                {peopleGoals.length === 0 && addingGoalType !== 'people' ? (
                  <div className="text-center py-4 bg-slate-50 rounded-lg border border-dashed text-sm text-muted-foreground">
                    No people goals yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {peopleGoals.map((goal) => (
                      editingGoal?.id !== goal.id && (
                        <GoalCard
                          key={goal.id}
                          goal={goal}
                          onEdit={(g) => { setEditingGoal(g); setAddingGoalType(null); }}
                          onDelete={handleDeleteGoal}
                          canEdit={canEditGoals && (goal.status === 'draft' || goal.status === 'rejected')}
                        />
                      )
                    ))}
                  </div>
                )}
              </div>
              </>
                )
              )}

              {/* Mid-Year Review Workflow - New Start/Leave UI */}
              {midYearReviewStatus === 'in_progress' && showMidYearReview && (
                <div className="space-y-4 border-2 border-amber-200 bg-amber-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-10 w-10 rounded-full bg-amber-600 text-white flex items-center justify-center">
                        <Clock className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-amber-700">Mid-Year Review in Progress</h4>
                        <p className="text-sm text-amber-600">Fill in your KPI comments below to complete your review</p>
                      </div>
                    </div>
                    <Badge className="bg-amber-600">In Progress</Badge>
                  </div>

                  <ReviewSection
                    goals={goals}
                    review={review}
                    midYearComments={midYearComments}
                    setMidYearComments={setMidYearComments}
                    yearEndComments={yearEndComments}
                    setYearEndComments={setYearEndComments}
                    onSubmitMidYear={handleShowSubmitConfirmation}
                    onSubmitYearEnd={() => handleSubmitReview('year_end')}
                    submitting={midYearReviewSubmitting}
                    showMidYear={true}
                    showYearEnd={false}
                    hideMidYearSubmit={true}
                  />

                  <div className="flex justify-between items-center pt-4 border-t border-amber-200">
                    <Button
                      variant="outline"
                      onClick={handleLeaveMidYearReview}
                      disabled={midYearReviewSubmitting}
                      className="text-amber-700 border-amber-300 hover:bg-amber-100"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Leave Review (Save progress)
                    </Button>
                    {/* Submit button moved here to be at same level as Leave.
                        Show unless the review has been FINALLY submitted (drafts still need to submit). */}
                    {review?.mid_year_feedback?.submission_status !== 'submitted' && (
                      <div className="flex flex-col items-end gap-1">
                        {!allMidYearCommentsFilled && (
                          <p className="text-xs text-amber-700">
                            Add a comment on every KPI above before submitting.
                          </p>
                        )}
                        <Button
                          onClick={handleShowSubmitConfirmation}
                          disabled={midYearReviewSubmitting || !allMidYearCommentsFilled}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          {midYearReviewSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                          Submit Mid-Year Comments
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Submit Confirmation Dialog */}
              <Dialog open={showSubmitConfirmation} onOpenChange={setShowSubmitConfirmation}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-600" />
                      Confirm Submission
                    </DialogTitle>
                    <DialogDescription className="text-base pt-3">
                      You will not be able to resubmit. Are there any changes required?
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex justify-end gap-3 pt-6">
                    <Button
                      variant="outline"
                      onClick={() => setShowSubmitConfirmation(false)}
                      disabled={midYearReviewSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCompleteMidYearReview}
                      disabled={midYearReviewSubmitting}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {midYearReviewSubmitting ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      Proceed
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Mid-Year Review Submitted Confirmation */}
              {midYearReviewStatus === 'submitted' && showMidYearReview && (
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-center gap-3 text-center">
                      <div className="h-12 w-12 rounded-full bg-green-600 text-white flex items-center justify-center">
                        <CheckCircle className="h-6 w-6" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-green-700">Mid-Year Review Submitted</CardTitle>
                        <CardDescription className="text-green-600 mt-1">Your review has been successfully submitted. Your manager will review your feedback.</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Button
                      onClick={() => {
                        setShowMidYearReview(false);
                        loadData();
                      }}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      Close
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Optimized Performance Review Section - Shows each goal once with both Mid-Year and Year-End */}
              {(showMidYearReview || showYearEndReview) && allApproved && midYearReviewStatus !== 'in_progress' && midYearReviewStatus !== 'submitted' && (
                <ReviewSection
                  goals={goals}
                  review={review}
                  midYearComments={midYearComments}
                  setMidYearComments={setMidYearComments}
                  yearEndComments={yearEndComments}
                  setYearEndComments={setYearEndComments}
                  onSubmitMidYear={() => handleSubmitReview('mid_year')}
                  onSubmitYearEnd={() => handleSubmitReview('year_end')}
                  submitting={submitting}
                  showMidYear={showMidYearReview && midYearReviewStatus !== 'in_progress'}
                  showYearEnd={showYearEndReview}
                />
              )}

              {/* Scorecard */}
              <Scorecard review={review} />
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
      
      {/* Scorecard Modal */}
      <ScorecardModal 
        open={showScorecardModal}
        onClose={() => setShowScorecardModal(false)}
        review={review}
        goals={goals}
        user={user}
      />
      
      {/* Calibration Modal - Shows employee's calibration details */}
      <Dialog open={showCalibrationModal} onOpenChange={setShowCalibrationModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Star className="h-6 w-6 text-amber-500" />
              Calibration
            </DialogTitle>
            <DialogDescription>
              {user?.name} • Year {selectedYear} Performance Calibration
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Performance Scores */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-blue-50 p-4 rounded-lg text-center border border-blue-200">
                <p className="text-2xl font-bold text-blue-700">{review?.goal_score?.toFixed(1) || '-'}</p>
                <p className="text-xs text-blue-600 font-medium">Goal Score</p>
                <p className="text-xs text-blue-500">80% Weight</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg text-center border border-purple-200">
                <p className="text-2xl font-bold text-purple-700">{review?.competency_score?.toFixed(1) || '-'}</p>
                <p className="text-xs text-purple-600 font-medium">Competency</p>
                <p className="text-xs text-purple-500">20% Weight</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg text-center border border-green-200">
                <p className="text-2xl font-bold text-green-700">{review?.overall_rating?.toFixed(1) || '-'}</p>
                <p className="text-xs text-green-600 font-medium">Overall Rating</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg text-center border border-amber-200">
                <p className="text-2xl font-bold text-amber-700">{review?.calibrated_rating?.toFixed(1) || '-'}</p>
                <p className="text-xs text-amber-600 font-medium">Calibrated Rating</p>
                <p className="text-xs text-amber-500">{review?.calibrated_rating ? 'Set by HR' : 'Pending HR'}</p>
              </div>
            </div>
            
            {/* HR Notes */}
            {review?.hr_notes && (
              <div className="bg-slate-50 rounded-lg p-4 border">
                <h4 className="text-sm font-medium mb-2">HR Notes</h4>
                <p className="text-sm text-muted-foreground">{review.hr_notes}</p>
              </div>
            )}
            
            {/* Signatures Section */}
            <div className="border rounded-lg p-4">
              <h4 className="text-sm font-medium mb-4">Signatures</h4>
              <div className="grid grid-cols-2 gap-4">
                {/* Manager Signature */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Manager Signature</Label>
                  {review?.manager_signature ? (
                    <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">{review.manager_signature}</span>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground bg-gray-50 p-3 rounded-lg">
                      Pending manager signature
                    </div>
                  )}
                </div>
                
                {/* Employee Signature */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Employee Signature</Label>
                  {review?.employee_signature ? (
                    <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">{review.employee_signature}</span>
                    </div>
                  ) : signingAs === 'employee' ? (
                    <div className="space-y-2">
                      <Input
                        placeholder="Enter your full name to sign"
                        value={signatureName}
                        onChange={(e) => setSignatureName(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSignCalibration} disabled={signingLoading || !signatureName.trim()}>
                          {signingLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PenTool className="h-4 w-4 mr-1" />}
                          Confirm
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setSigningAs(null); setSignatureName(''); }}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : review?.calibrated_rating ? (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => setSigningAs('employee')}
                    >
                      <PenTool className="h-4 w-4 mr-1" />
                      Sign as Employee
                    </Button>
                  ) : (
                    <div className="text-sm text-muted-foreground bg-gray-50 p-3 rounded-lg">
                      Available after HR calibration
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Status */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Status:</span>
                <Badge variant={review?.status === 'completed' ? 'default' : 'outline'}>
                  {review?.status === 'completed' ? 'Completed' : 'In Progress'}
                </Badge>
              </div>
              <Button variant="outline" onClick={() => setShowCalibrationModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
